import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "./OffersPage.css";
import Loader from "../layouts/components/Loader";
import { toast } from "react-hot-toast";
import AuthModal from "../layouts/components/AuthModal";

const API_BASE = "https://api.mariamlyluxe.com/public";

// token
const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("auth_token") ||
  localStorage.getItem("access_token") ||
  "";

// fetch helper
async function apiFetch(path, { method = "GET", body } = {}) {
  const headers = { Accept: "application/json" };
  const tok = getToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  const opts = { method, headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE}${path}`, opts);
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const err = new Error(data?.message || `Request failed: ${res.status}`);
    err.status = res.status;
    err.errors = data?.errors;
    throw err;
  }
  return data;
}

function formatPrice(n) {
  const val = Number(n) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(val);
}

function discountPct(regular, selling) {
  const r = Number(regular);
  const s = Number(selling);
  if (!r || !s || s >= r) return 0;
  return Math.round(((r - s) / r) * 100);
}

function CartIcon({
  size = 18,
  strokeWidth = 1.8,
  color = "#fff",
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <line
        x1="12" y1="4" x2="12" y2="8"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <line
        x1="10" y1="6" x2="14" y2="6"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path
        d="M6 8h13l-1.2 6.5a2 2 0 0 1-2 1.5H8a2 2 0 0 1-2-1.5L4 4H2"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="20" r="1.6" fill={color} />
      <circle cx="18" cy="20" r="1.6" fill={color} />
    </svg>
  );
}

function EyeIcon({
  size = 18,
  strokeWidth = 1.8,
  color = "#fff",
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function OffersPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // data states
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState("");

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);

  const [subcategories, setSubcategories] = useState([]);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);

  // filters (URL-synced)
  const [filters, setFilters] = useState({
    search: "",
    category_id: "",
    subcategory_id: "",
    brand_id: "",
    min_discount: "",
    sort: "discount_high",
  });

  // offers-pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  // filter toggle state - only for mobile
  const [filtersVisible, setFiltersVisible] = useState(false);

  // auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);

  // product quick view popup state
  const [showProductPopup, setShowProductPopup] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetailsLoading, setProductDetailsLoading] = useState(false);

  // banner image state
  const [bannerImage, setBannerImage] = useState(null);

  // categories
  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const data = await apiFetch("/api/categories");
      setCategories(Array.isArray(data) ? data : data?.categories || data?.data || []);
    } catch (e) {
      console.warn("Failed to load categories:", e);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // brands
  const loadBrands = async () => {
    setBrandsLoading(true);
    try {
      const data = await apiFetch("/api/brands");
      setBrands(Array.isArray(data) ? data : data?.brands || data?.data || []);
    } catch (e) {
      console.warn("Failed to load brands:", e);
    } finally {
      setBrandsLoading(false);
    }
  };

  // subcategories
  const loadSubcategories = async (categoryId = null) => {
    setSubcategoriesLoading(true);
    try {
      let data;
      if (categoryId) {
        data = await apiFetch(`/api/categories/${categoryId}/subcategories`);
      } else {
        data = await apiFetch("/api/subcategories");
      }
      setSubcategories(Array.isArray(data) ? data : data?.subcategories || data?.data || []);
    } catch (e) {
      console.warn("Failed to load subcategories:", e);
    } finally {
      setSubcategoriesLoading(false);
    }
  };

  // products (combine hot offers with discounted on default view)
  const loadProducts = async () => {
    setProductsLoading(true);
    setProductsError("");
    try {
      const params = new URLSearchParams();

      // Check if any meaningful filters are applied (excluding default sort)
      const hasFilters =
        filters.category_id ||
        filters.subcategory_id ||
        filters.brand_id ||
        filters.search ||
        filters.min_discount ||
        (filters.sort && filters.sort !== "discount_high");

      // Default view (no filters, first page):
      // call BOTH hot-offers and discounted (sort=discount_high&page=1)
      if (!hasFilters && currentPage === 1) {
        const [hotData, discountedData] = await Promise.all([
          apiFetch("/api/products/hot-offers"),
          apiFetch("/api/products/discounted?sort=discount_high&page=1"),
        ]);

        const hotList = Array.isArray(hotData?.hot_offer_products)
          ? hotData.hot_offer_products
          : hotData?.products || hotData?.data || [];

        const discountedList =
          discountedData?.discounted_products?.data ||
          discountedData?.products ||
          discountedData?.data || [];

        const hotArr = Array.isArray(hotList) ? hotList : [];
        const discArr = Array.isArray(discountedList) ? discountedList : [];

        // merge: hot offers first, then discounted without duplicates by id
        const ids = new Set(hotArr.map((p) => p.id));
        const merged = hotArr.concat(discArr.filter((p) => !ids.has(p.id)));

        setProducts(merged);

        setTotalPages(
          discountedData?.discounted_products?.last_page ||
          discountedData?.last_page ||
          discountedData?.total_pages ||
          1
        );
        setTotalProducts(
          discountedData?.discounted_products?.total ||
          discountedData?.total ||
          merged.length ||
          0
        );
        return;
      }

      // Otherwise use discounted products with filters and pagination only
      if (filters.category_id) params.append("category_id", filters.category_id);
      if (filters.subcategory_id) params.append("subcategory_id", filters.subcategory_id);
      if (filters.brand_id) params.append("brand_id", filters.brand_id);
      if (filters.search) params.append("search", filters.search);
      if (filters.min_discount) params.append("min_discount", filters.min_discount);
      if (filters.sort) params.append("sort", filters.sort);
      else params.append("sort", "discount_high");
      params.append("page", currentPage);

      const data = await apiFetch(`/api/products/discounted?${params.toString()}`);

      const list = data?.discounted_products?.data || data?.products || data?.data || [];
      const normalized = Array.isArray(list) ? list : [];
      setProducts(normalized);

      setTotalPages(
        data?.discounted_products?.last_page ||
        data?.last_page ||
        data?.total_pages ||
        1
      );
      setTotalProducts(
        data?.discounted_products?.total ||
        data?.total ||
        normalized.length ||
        0
      );
    } catch (e) {
      setProductsError(e.message || "Failed to load offers.");
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const loadBannerImage = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/pages-banners/offers`, {
        headers: { Accept: "application/json" }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.banner && data.banner.image_url) {
          setBannerImage(data.banner.image_url);
        }
      }
    } catch (e) {
      console.warn("Failed to load banner image:", e);
      // Fallback to default image if API fails
      setBannerImage('../images/crosswalk.jpg');
    }
  };

  // sync filters from URL
  useEffect(() => {
    const url = {
      search: searchParams.get("search") || "",
      category_id: searchParams.get("category_id") || "",
      subcategory_id: searchParams.get("subcategory_id") || "",
      brand_id: searchParams.get("brand_id") || "",
      min_discount: searchParams.get("min_discount") || "",
      sort: searchParams.get("sort") || "discount_high",
    };
    setFilters(url);
  }, [searchParams]);

  useEffect(() => {
    loadCategories();
    loadBrands();
    loadSubcategories();
    loadBannerImage();
  }, []);

  // Load subcategories when category changes
  useEffect(() => {
    if (filters.category_id) {
      loadSubcategories(filters.category_id);
    } else {
      loadSubcategories();
    }
  }, [filters.category_id]);

  useEffect(() => {
    loadProducts();
  }, [filters, currentPage]);

  const updateURL = (next) => {
    const p = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p.set(k, v);
    });
    setSearchParams(p);
  };

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    setCurrentPage(1);
    updateURL(next);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    handleFilterChange("search", filters.search);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      search: "",
      category_id: "",
      subcategory_id: "",
      brand_id: "",
      min_discount: "",
      sort: "discount_high",
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    updateURL(clearedFilters);
  };

  const clearCategory = () => {
    const next = { ...filters, category_id: "", subcategory_id: "" };
    setFilters(next);
    setCurrentPage(1);
    updateURL(next);
  };
  const clearBrand = () => handleFilterChange("brand_id", "");
  const clearSubcategory = () => handleFilterChange("subcategory_id", "");

  // cart loading states
  const [cartLoading, setCartLoading] = useState({});

  // add to cart functionality
  const handleAddToCart = async (product) => {
    const productId = product.id;
    const token = getToken();
    
    if (!token) {
      setShowAuthModal(true);
      return;
    }
    
    setCartLoading(prev => ({ ...prev, [productId]: true }));
    
    try {
      const response = await apiFetch('/api/cart', {
        method: 'POST',
        body: { 
          product_id: productId, 
          quantity: 1 
        }
      });
      
      toast.success(`${product.name} added to cart successfully!`);
      
    } catch (error) {
      console.error('Failed to add to cart:', error);
      toast.error('Failed to add item to cart. Please try again.');
    } finally {
      setCartLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Handle successful login from AuthModal
  const handleLoginSuccess = () => {
    setShowAuthModal(false);
    // Optionally refresh data or update UI state after login
  };

  // Handle product quick view
  const handleProductQuickView = async (product) => {
    setShowProductPopup(true);
    setProductDetailsLoading(true);
    setSelectedProduct(null);
    
    try {
      const data = await apiFetch(`/api/products/${product.id}`);
      if (data.success && data.product) {
        setSelectedProduct(data.product);
      } else {
        toast.error('Failed to load product details');
        setShowProductPopup(false);
      }
    } catch (error) {
      console.error('Failed to fetch product details:', error);
      toast.error('Failed to load product details');
      setShowProductPopup(false);
    } finally {
      setProductDetailsLoading(false);
    }
  };

  // Close product popup
  const closeProductPopup = () => {
    setShowProductPopup(false);
    setSelectedProduct(null);
    setProductDetailsLoading(false);
  };

  return (
    <section className="special-offers-page">
      {/* Banner Section */}
      <div 
        className="offers-hero"
        style={{
          backgroundImage: bannerImage ? `url(${bannerImage})` : 'url(../images/crosswalk.jpg)'
        }}
      >
        <div className="offers-hero-content">
          <h1 className="offers-hero-title">Special Offers</h1>
          <nav className="offers-breadcrumb">
            <Link to="/">Mariamly</Link>
            <span className="offers-breadcrumb-separator">›</span>
            <span className="offers-breadcrumb-current">Offers</span>
          </nav>
        </div>
      </div>

      <div className="offers-main-container">
        <div className="offers-main-content">
          {/* Mobile Filter Toggle */}
          <div className="offers-mobile-filter-toggle">
            <button 
              className="offers-toggle-filters-btn"
              onClick={() => setFiltersVisible(!filtersVisible)}
              aria-label={filtersVisible ? "Hide filters" : "Show filters"}
            >
              <span>Filters</span>
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none"
                className={`offers-toggle-icon ${filtersVisible ? 'expanded' : ''}`}
              >
                <path 
                  d="M6 9l6 6 6-6" 
                  stroke="#d19e90" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="offers-main-layout">
            {/* DESKTOP SIDEBAR FILTERS */}
            <aside className="offers-desktop-sidebar">
              {/* Search */}
              <div className="offers-filter-section">
                <form onSubmit={onSearchSubmit} className="offers-filter-search">
                  <input
                    type="text"
                    placeholder="Search offers"
                    value={filters.search}
                    onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                  />
                  <button type="submit" aria-label="Search">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M21 21l-4.2-4.2" stroke="#7B8494" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="11" cy="11" r="7" stroke="#7B8494" strokeWidth="2" />
                    </svg>
                  </button>
                </form>
              </div>

              {/* Minimum Discount */}
              <div className="offers-filter-section">
                <div className="offers-filter-title-with-icon">
                  <h3>Minimum Discount</h3>
                  <span className="offers-filter-icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 5h18l-7 8v5l-4 2v-7L3 5z"
                        stroke="#d19e90"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  </span>
                </div>

                <div className="offers-offers-discount-options">
                  <button
                    className={`offers-discount-option ${!filters.min_discount ? "active" : ""}`}
                    onClick={() => handleFilterChange("min_discount", "")}
                    type="button"
                  >
                    All Discounts
                  </button>
                  <button
                    className={`offers-discount-option ${filters.min_discount === "10" ? "active" : ""}`}
                    onClick={() => handleFilterChange("min_discount", "10")}
                    type="button"
                  >
                    10% or more
                  </button>
                  <button
                    className={`offers-discount-option ${filters.min_discount === "20" ? "active" : ""}`}
                    onClick={() => handleFilterChange("min_discount", "20")}
                    type="button"
                  >
                    20% or more
                  </button>
                  <button
                    className={`offers-discount-option ${filters.min_discount === "30" ? "active" : ""}`}
                    onClick={() => handleFilterChange("min_discount", "30")}
                    type="button"
                  >
                    30% or more
                  </button>
                  <button
                    className={`offers-discount-option ${filters.min_discount === "50" ? "active" : ""}`}
                    onClick={() => handleFilterChange("min_discount", "50")}
                    type="button"
                  >
                    50% or more
                  </button>
                </div>
              </div>

              {/* Categories */}
              <div className="offers-filter-section">
                <h3 className="offers-filter-title">Categories</h3>
                <div className="offers-category-list">
                  {categoriesLoading ? (
                    <Loader />
                  ) : (
                    <>
                      <button
                        className={`offers-category-row ${!filters.category_id ? "active" : ""}`}
                        onClick={clearCategory}
                        type="button"
                      >
                        <span>All</span>
                        <i className="offers-category-chevron">&rsaquo;</i>
                      </button>
                      {categories.map((c) => (
                        <div key={c.id}>
                          <button
                            className={`offers-category-row ${
                              filters.category_id === String(c.id) ? "active" : ""
                            }`}
                            onClick={() => handleFilterChange("category_id", String(c.id))}
                            type="button"
                          >
                            <span>{c.name}</span>
                            <i className="offers-category-chevron">&rsaquo;</i>
                          </button>
                          {/* Show subcategories for this category */}
                          {filters.category_id === String(c.id) && (
                            <div className="offers-subcategories-list">
                              {subcategoriesLoading ? (
                                <div className="offers-subcategory-loading">
                                  <Loader size={16} />
                                </div>
                              ) : (
                                <>
                                  <button
                                    className={`offers-subcategory-row ${!filters.subcategory_id ? "active" : ""}`}
                                    onClick={clearSubcategory}
                                    type="button"
                                  >
                                    <span>All {c.name}</span>
                                    <i className="offers-category-chevron">&rsaquo;</i>
                                  </button>
                                  {subcategories.map((sc) => (
                                    <button
                                      key={sc.id}
                                      className={`offers-subcategory-row ${
                                        filters.subcategory_id === String(sc.id) ? "active" : ""
                                      }`}
                                      onClick={() => handleFilterChange("subcategory_id", String(sc.id))}
                                      type="button"
                                    >
                                      <span>{sc.name}</span>
                                      <i className="offers-category-chevron">&rsaquo;</i>
                                    </button>
                                  ))}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>


              {/* Brands */}
              <div className="offers-filter-section">
                <h3 className="offers-filter-title">Brands</h3>
                <div className="offers-category-list">
                  {brandsLoading ? (
                    <Loader />
                  ) : (
                    <>
                      <button
                        className={`offers-category-row ${!filters.brand_id ? "active" : ""}`}
                        onClick={clearBrand}
                        type="button"
                      >
                        <span>All</span>
                        <i className="offers-category-chevron">&rsaquo;</i>
                      </button>
                      {brands.map((b) => (
                        <button
                          key={b.id}
                          className={`offers-category-row ${
                            filters.brand_id === String(b.id) ? "active" : ""
                          }`}
                          onClick={() => handleFilterChange("brand_id", String(b.id))}
                          type="button"
                        >
                          <span>{b.name}</span>
                          <i className="offers-category-chevron">&rsaquo;</i>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Sort Options */}
              <div className="offers-filter-section">
                <h3 className="offers-filter-title">Sort By</h3>
                <div className="offers-offers-sort-options">
                  <button
                    className={`offers-sort-option ${filters.sort === "discount_high" ? "active" : ""}`}
                    onClick={() => handleFilterChange("sort", "discount_high")}
                    type="button"
                  >
                    Highest Discount
                  </button>
                  <button
                    className={`offers-sort-option ${filters.sort === "price_low" ? "active" : ""}`}
                    onClick={() => handleFilterChange("sort", "price_low")}
                    type="button"
                  >
                    Price: Low to High
                  </button>
                  <button
                    className={`offers-sort-option ${filters.sort === "price_high" ? "active" : ""}`}
                    onClick={() => handleFilterChange("sort", "price_high")}
                    type="button"
                  >
                    Price: High to Low
                  </button>
                </div>
              </div>
            </aside>

            {/* MOBILE FILTERS */}
            <div className={`offers-mobile-filters ${filtersVisible ? 'visible' : 'hidden'}`}>
              {/* Search */}
              <div className="offers-filter-section">
                <form onSubmit={onSearchSubmit} className="offers-filter-search">
                  <input
                    type="text"
                    placeholder="Search offers"
                    value={filters.search}
                    onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                  />
                  <button type="submit" aria-label="Search">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M21 21l-4.2-4.2" stroke="#7B8494" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="11" cy="11" r="7" stroke="#7B8494" strokeWidth="2" />
                    </svg>
                  </button>
                </form>
              </div>

              {/* Minimum Discount */}
              <div className="offers-filter-section">
                <div className="offers-filter-title-with-icon">
                  <h3>Minimum Discount</h3>
                  <span className="offers-filter-icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 5h18l-7 8v5l-4 2v-7L3 5z"
                        stroke="#d19e90"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  </span>
                </div>

                <div className="offers-offers-discount-options">
                  <button
                    className={`offers-discount-option ${!filters.min_discount ? "active" : ""}`}
                    onClick={() => handleFilterChange("min_discount", "")}
                    type="button"
                  >
                    All Discounts
                  </button>
                  <button
                    className={`offers-discount-option ${filters.min_discount === "10" ? "active" : ""}`}
                    onClick={() => handleFilterChange("min_discount", "10")}
                    type="button"
                  >
                    10% or more
                  </button>
                  <button
                    className={`offers-discount-option ${filters.min_discount === "20" ? "active" : ""}`}
                    onClick={() => handleFilterChange("min_discount", "20")}
                    type="button"
                  >
                    20% or more
                  </button>
                  <button
                    className={`offers-discount-option ${filters.min_discount === "30" ? "active" : ""}`}
                    onClick={() => handleFilterChange("min_discount", "30")}
                    type="button"
                  >
                    30% or more
                  </button>
                  <button
                    className={`offers-discount-option ${filters.min_discount === "50" ? "active" : ""}`}
                    onClick={() => handleFilterChange("min_discount", "50")}
                    type="button"
                  >
                    50% or more
                  </button>
                </div>
              </div>

              {/* Categories */}
              <div className="offers-filter-section">
                <h3 className="offers-filter-title">Categories</h3>
                <div className="offers-category-list">
                  {categoriesLoading ? (
                    <Loader />
                  ) : (
                    <>
                      <button
                        className={`offers-category-row ${!filters.category_id ? "active" : ""}`}
                        onClick={clearCategory}
                        type="button"
                      >
                        <span>All</span>
                        <i className="offers-category-chevron">&rsaquo;</i>
                      </button>
                      {categories.map((c) => (
                        <div key={c.id}>
                          <button
                            className={`offers-category-row ${
                              filters.category_id === String(c.id) ? "active" : ""
                            }`}
                            onClick={() => handleFilterChange("category_id", String(c.id))}
                            type="button"
                          >
                            <span>{c.name}</span>
                            <i className="offers-category-chevron">&rsaquo;</i>
                          </button>
                          {/* Show subcategories for this category */}
                          {filters.category_id === String(c.id) && (
                            <div className="offers-subcategories-list">
                              {subcategoriesLoading ? (
                                <div className="offers-subcategory-loading">
                                  <Loader size={16} />
                                </div>
                              ) : (
                                <>
                                  <button
                                    className={`offers-subcategory-row ${!filters.subcategory_id ? "active" : ""}`}
                                    onClick={clearSubcategory}
                                    type="button"
                                  >
                                    <span>All {c.name}</span>
                                    <i className="offers-category-chevron">&rsaquo;</i>
                                  </button>
                                  {subcategories.map((sc) => (
                                    <button
                                      key={sc.id}
                                      className={`offers-subcategory-row ${
                                        filters.subcategory_id === String(sc.id) ? "active" : ""
                                      }`}
                                      onClick={() => handleFilterChange("subcategory_id", String(sc.id))}
                                      type="button"
                                    >
                                      <span>{sc.name}</span>
                                      <i className="offers-category-chevron">&rsaquo;</i>
                                    </button>
                                  ))}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>


              {/* Brands */}
              <div className="offers-filter-section">
                <h3 className="offers-filter-title">Brands</h3>
                <div className="offers-category-list">
                  {brandsLoading ? (
                    <Loader />
                  ) : (
                    <>
                      <button
                        className={`offers-category-row ${!filters.brand_id ? "active" : ""}`}
                        onClick={clearBrand}
                        type="button"
                      >
                        <span>All</span>
                        <i className="offers-category-chevron">&rsaquo;</i>
                      </button>
                      {brands.map((b) => (
                        <button
                          key={b.id}
                          className={`offers-category-row ${
                            filters.brand_id === String(b.id) ? "active" : ""
                          }`}
                          onClick={() => handleFilterChange("brand_id", String(b.id))}
                          type="button"
                        >
                          <span>{b.name}</span>
                          <i className="offers-category-chevron">&rsaquo;</i>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Sort Options */}
              <div className="offers-filter-section">
                <h3 className="offers-filter-title">Sort By</h3>
                <div className="offers-offers-sort-options">
                  <button
                    className={`offers-sort-option ${filters.sort === "discount_high" ? "active" : ""}`}
                    onClick={() => handleFilterChange("sort", "discount_high")}
                    type="button"
                  >
                    Highest Discount
                  </button>
                  <button
                    className={`offers-sort-option ${filters.sort === "price_low" ? "active" : ""}`}
                    onClick={() => handleFilterChange("sort", "price_low")}
                    type="button"
                  >
                    Price: Low to High
                  </button>
                  <button
                    className={`offers-sort-option ${filters.sort === "price_high" ? "active" : ""}`}
                    onClick={() => handleFilterChange("sort", "price_high")}
                    type="button"
                  >
                    Price: High to Low
                  </button>
                </div>
              </div>
            </div>

            {/* PRODUCTS SECTION */}
            <div className="offers-products-section">
            <div className="offers-products-header">
              <h2>Special Offers</h2>
            </div>

            {productsLoading ? (
              <div className="offers-products-loading">
                <Loader />
                <p>Loading offers…</p>
              </div>
            ) : productsError ? (
              <div className="offers-products-error">
                <p>{productsError}</p>
                <button onClick={loadProducts}>Try Again</button>
              </div>
            ) : products.length === 0 ? (
              <div className="offers-products-empty">
                <p>No offers found.</p>
              </div>
            ) : (
              <>
                <div className="offers-products-grid">
                  {products.map((p) => {
                    const reg = p.regular_price || p.mrp || p.price;
                    const sell = p.selling_price || p.price;
                    const pct = discountPct(reg, sell);
                    return (
                      <div key={p.id} className="offers-product-card">
                        <Link to={`/product/${p.slug || p.id}`} className="offers-product-link">
                          <div className="offers-product-image">
                            {p.image_url ? (
                              <img src={p.image_url} alt={p.name} />
                            ) : (
                              <div className="offers-product-no-image">No Image</div>
                            )}
                            {/* Discount Badge */}
                            {pct > 0 && (
                              <div className="offers-product-discount-badge">
                                -{pct}%
                              </div>
                            )}
                            {/* Quick view button */}
                            <button 
                              className="offers-quick-view-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                handleProductQuickView(p);
                              }}
                              aria-label="Quick view"
                              title="Quick view"
                            >
                              <EyeIcon size={20} />
                            </button>
                          </div>
                          <div className="offers-product-info">
                            <div className="offers-product-top">
                              <h3 className="offers-product-name">{p.name}</h3>
                              <button 
                                className="offers-add-to-cart-btn"
                                aria-label={cartLoading[p.id] ? "Adding..." : "Add to cart"}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleAddToCart(p);
                                }}
                                disabled={cartLoading[p.id]}
                                title={cartLoading[p.id] ? "Adding..." : "Add to cart"}
                              >
                                {cartLoading[p.id] ? <Loader size={18} /> : <CartIcon />}
                              </button>
                            </div>
                            <div className="offers-product-pricing-info">
                              <div className="offers-product-pricing-left">
                                {reg && Number(reg) !== Number(sell) && (
                                  <span className="offers-product-original-price" style={{ color: '#e74c3c', textDecoration: 'line-through' }}>{formatPrice(reg)}</span>
                                )}
                                <span className="offers-product-price">{formatPrice(sell)}</span>
                              </div>
                              {reg && Number(reg) !== Number(sell) && (
                                <span className="offers-product-savings">
                                  Save {pct}%  
                                </span>
                              )}
                            </div>
                          </div>
                        </Link>
                      </div>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="offers-pagination">
                    <button
                      className="offers-pagination-btn"
                      onClick={() => setCurrentPage((v) => Math.max(1, v - 1))}
                      disabled={currentPage === 1}
                    >
                      &lt;
                    </button>
                    <span className="offers-pagination-info">
                      {currentPage} of {totalPages}
                    </span>
                    <button
                      className="offers-pagination-btn"
                      onClick={() => setCurrentPage((v) => Math.min(totalPages, v + 1))}
                      disabled={currentPage === totalPages}
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* AuthModal component */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Product Quick View Popup */}
      {showProductPopup && (
        <div className="offers-product-popup-overlay" onClick={closeProductPopup}>
          <div className="offers-product-popup" onClick={(e) => e.stopPropagation()}>
            <button 
              className="offers-popup-close"
              onClick={closeProductPopup}
              aria-label="Close popup"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M18 6L6 18M6 6l12 12" 
                  stroke="#666" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            
            {productDetailsLoading ? (
              <div className="offers-popup-loading">
                <Loader />
                <p>Loading product details...</p>
              </div>
            ) : selectedProduct ? (
              <div className="offers-popup-content">
              <div className="offers-popup-image">
                {selectedProduct.image_url ? (
                  <img src={selectedProduct.image_url} alt={selectedProduct.name} />
                ) : (
                  <div className="offers-popup-no-image">No Image</div>
                )}
              </div>
              
              <div className="offers-popup-details">
                <div className="offers-popup-header">
                  <h2 className="offers-popup-title">{selectedProduct.name}</h2>
                  {selectedProduct.sku && (
                    <span className="offers-popup-sku">SKU: {selectedProduct.sku}</span>
                  )}
                </div>
                
                {/* Brand and Categories */}
                <div className="offers-popup-meta">
                  {selectedProduct.brand && (
                    <div className="offers-popup-meta-item">
                      <span className="offers-popup-meta-label">Brand:</span>
                      <span className="offers-popup-meta-value">{selectedProduct.brand.name}</span>
                    </div>
                  )}
                  {selectedProduct.categories && selectedProduct.categories.length > 0 && (
                    <div className="offers-popup-meta-item">
                      <span className="offers-popup-meta-label">Category:</span>
                      <span className="offers-popup-meta-value">
                        {selectedProduct.categories.map(cat => cat.name).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Description */}
                {(selectedProduct.desc || selectedProduct.description) && (
                  <div className="offers-popup-section">
                    <h4 className="offers-popup-section-title">Description</h4>
                    <p className="offers-popup-description">
                      {selectedProduct.desc || selectedProduct.description}
                    </p>
                  </div>
                )}
                
                {/* Product Details */}
                <div className="offers-popup-section">
                  <h4 className="offers-popup-section-title">Product Details</h4>
                  <div className="offers-popup-details-grid">
                    {selectedProduct.weight && (
                      <div className="offers-popup-detail-item">
                        <span className="offers-popup-detail-label">Weight:</span>
                        <span className="offers-popup-detail-value">{selectedProduct.weight}</span>
                      </div>
                    )}
                    {selectedProduct.ingredients && (
                      <div className="offers-popup-detail-item offers-popup-detail-full">
                        <span className="offers-popup-detail-label">Ingredients:</span>
                        <span className="offers-popup-detail-value">{selectedProduct.ingredients}</span>
                      </div>
                    )}
                    {selectedProduct.usage_instructions && (
                      <div className="offers-popup-detail-item offers-popup-detail-full">
                        <span className="offers-popup-detail-label">Usage Instructions:</span>
                        <span className="offers-popup-detail-value">{selectedProduct.usage_instructions}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Variants */}
                {selectedProduct.variants && selectedProduct.variants.length > 0 && (
                  <div className="offers-popup-section">
                    <h4 className="offers-popup-section-title">Available Variants</h4>
                    <div className="offers-popup-variants">
                      {selectedProduct.variants.map((variant) => (
                        <div key={variant.id} className="offers-popup-variant">
                          <div 
                            className="offers-popup-variant-color"
                            style={{ backgroundColor: variant.hex_color }}
                            title={variant.color}
                          ></div>
                          <span className="offers-popup-variant-name">{variant.color}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Pricing */}
                <div className="offers-popup-pricing">
                  {(() => {
                    const reg = selectedProduct.regular_price || selectedProduct.mrp || selectedProduct.price;
                    const sell = selectedProduct.selling_price || selectedProduct.price;
                    const pct = discountPct(reg, sell);
                    
                    return (
                      <>
                        {reg && Number(reg) !== Number(sell) && (
                          <span className="offers-popup-original-price">
                            {formatPrice(reg)}
                          </span>
                        )}
                        <span className="offers-popup-price">{formatPrice(sell)}</span>
                        {reg && Number(reg) !== Number(sell) && (
                          <span className="offers-popup-discount">
                            Save {pct}%
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
                
                {/* Actions */}
                <div className="offers-popup-actions">
                  <button 
                    className="offers-popup-view-btn"
                    onClick={() => {
                      closeProductPopup();
                      window.location.href = `/product/${selectedProduct.slug || selectedProduct.id}`;
                    }}
                  >
                    View Full Details
                  </button>
                  
                  
                </div>
              </div>
            </div>
            ) : (
              <div className="offers-popup-error">
                <p>Failed to load product details</p>
                <button onClick={closeProductPopup}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
